import {
  useEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type DragEvent,
  type FormEvent,
} from 'react';
import { Controller, useForm, useWatch, type FieldError } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  CARGO_PROBLEM_COMMENT_MAX,
  CargoProblemMessageCreateInputSchema,
  ErrorCode,
  isCargoProblemDocumentMime,
  type CargoProblemMessageCreateInput,
  type CargoProblemWorkspaceMessageItem,
} from '@magimp/shared';
import { Button, Icon, IconButton, Textarea } from '../../../ui/index.js';
import { ApiClientError } from '../../../api/errors.js';
import { errorMessage } from '../../../api/errors.js';
import { useToastStore } from '../../../stores/toast.js';
import { usePostCargoProblemMessage } from '../queries.js';
import { useCargoProblemMessageAttachments } from '../useCargoProblemMessageAttachments.js';
import {
  cargoProblemFileIcon,
  cargoProblemFileSize,
  cargoProblemFileType,
} from './CargoProblemAttachments.js';

/**
 * @aiInvariant one user intent is one request: the typed body and every
 *   selected attachment are committed together, and the local draft survives a
 *   failure so nothing typed is lost.
 * @aiInvariant the captured intent is frozen for the WHOLE send, including the
 *   expired-key reissue that runs between two commits. An edit or a removal
 *   accepted mid-send would either be erased by the success reset or commit a
 *   set the sender never saw, so every control is disabled until the send
 *   settles — the mutation's own pending flag leaves the composer live in the
 *   gap between the refused first commit and the second one.
 * @aiInvariant the idempotency key belongs to the SEMANTIC draft, not to the
 *   request: one key covers every commit of the same body, reply target and
 *   attachment set — the expired-key second commit and each manual retry after
 *   an ambiguous failure alike — so a send that was written but answered with
 *   an error cannot be posted twice. Editing any of the three before the retry
 *   mints a new key instead, because backend dedupe would otherwise answer with
 *   the message the sender just rewrote and silently drop the change.
 */
export function CargoProblemMessageComposer({
  problemId,
  canPost,
  canAttach,
  replyTo,
  onCancelReply,
}: {
  problemId: string;
  canPost: boolean;
  canAttach: boolean;
  replyTo: CargoProblemWorkspaceMessageItem | null;
  onCancelReply: () => void;
}) {
  const { t } = useTranslation('cargoProblems');
  const showToast = useToastStore((state) => state.show);
  const post = usePostCargoProblemMessage(problemId);
  const attachments = useCargoProblemMessageAttachments(problemId);
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const draft = useRef<{ key: string; signature: string } | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  // WHY: the form value IS the wire payload. Only the body is typed; the key,
  // the reply target and the stored attachments are composed into it at submit
  // (`submit` below), so the shared schema validates exactly what is committed
  // instead of a hand-checked subset. Validation runs on submit only — the
  // composed fields are refreshed there, so re-validating a keystroke would
  // judge the draft against the previous send's context.
  const {
    control,
    formState: { errors, isSubmitting },
    getValues,
    handleSubmit,
    reset,
    setValue,
  } = useForm<CargoProblemMessageCreateInput>({
    resolver: zodResolver(CargoProblemMessageCreateInputSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: { clientMessageId: '', body: '' },
  });
  const body = useWatch({ control, name: 'body' }) ?? '';

  useEffect(() => {
    if (!replyTo) return;
    const frame = requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (!textarea?.isConnected || textarea.matches(':disabled') || textarea.closest('[hidden]')) {
        return;
      }
      textarea.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [replyTo]);

  const hasBody = body.trim().length > 0;
  const needsText =
    !hasBody && attachments.items.some((item) => isCargoProblemDocumentMime(item.file.type));
  const canSubmit =
    (hasBody || attachments.items.length > 0) && !needsText && attachments.ready && !isSubmitting;

  function select(files: readonly File[]) {
    if (isSubmitting) return;
    if (files.length === 0) return;
    const error = attachments.add(files);
    setProblem(error ? t(`files.errors.${error}`) : null);
  }

  const send = handleSubmit(async (payload) => {
    setProblem(null);
    try {
      await attachments.sendWith((uploaded) =>
        post.mutateAsync({
          ...payload,
          // WHY: an expired object key is reissued between the two commits of
          // one send, so each commit binds the freshly stored set rather than
          // the one composed at submit. Both travel through
          // `postCargoProblemWorkspaceMessage`, which parses every payload
          // against this same shared schema before it leaves the client.
          ...(uploaded.length > 0 ? { attachments: uploaded } : {}),
        }),
      );
      draft.current = null;
      reset({ body: '' });
      attachments.clear();
      onCancelReply();
      showToast({ tone: 'success', text: t('message.sent') });
    } catch (error) {
      setProblem(describe(error));
    }
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit) return;
    const typed = (getValues('body') ?? '').trim();
    const stored = attachments.items.flatMap((item) => (item.uploaded ? [item.uploaded] : []));
    setValue('clientMessageId', keyFor(typed, replyTo?.id, attachments.items));
    setValue('body', typed.length > 0 ? typed : undefined);
    setValue('replyToId', replyTo?.id);
    setValue('attachments', stored.length > 0 ? stored : undefined);
    void send();
  }

  /**
   * WHY: the signature names WHAT is being sent, so a re-upload that only
   * refreshes an expired object key keeps the key and stays deduped, while a
   * rewritten body, a changed reply target or a changed file set becomes a new
   * message instead of resolving to the stale row.
   */
  function keyFor(
    trimmedBody: string,
    replyToId: string | undefined,
    items: readonly { id: string }[],
  ): string {
    const signature = JSON.stringify([
      trimmedBody,
      replyToId ?? null,
      items.map((item) => item.id),
    ]);
    if (draft.current?.signature !== signature) {
      draft.current = { key: crypto.randomUUID(), signature };
    }
    return draft.current.key;
  }

  function describe(error: unknown): string {
    const rejectedParent =
      replyTo !== null &&
      error instanceof ApiClientError &&
      (error.code === ErrorCode.NOT_FOUND || error.code === ErrorCode.VALIDATION_ERROR);
    return rejectedParent ? t('message.replyUnavailable') : errorMessage(error);
  }

  // The composer gates every schema rule it can before the send, so the only
  // issue a sender normally reaches is the length ceiling; the rest is the
  // backstop copy for the same two rules.
  function describeBody(error: FieldError | undefined): string | null {
    if (!error) return null;
    if (error.type === 'too_big') return t('message.tooLong', { max: CARGO_PROBLEM_COMMENT_MAX });
    return needsText ? t('message.documentNeedsText') : t('message.empty');
  }

  if (!canPost) return null;
  const alertText = describeBody(errors.body) ?? problem;
  return (
    <form
      className="cargo-problem-composer"
      onSubmit={submit}
      onDrop={(event: DragEvent<HTMLFormElement>) => {
        if (!canAttach || isSubmitting) return;
        event.preventDefault();
        select(Array.from(event.dataTransfer.files));
      }}
      onDragOver={(event: DragEvent<HTMLFormElement>) => {
        if (canAttach && !isSubmitting) event.preventDefault();
      }}
      onPaste={(event: ClipboardEvent<HTMLFormElement>) => {
        if (!canAttach || isSubmitting) return;
        const pasted = Array.from(event.clipboardData.files);
        if (pasted.length > 0) select(pasted);
      }}
    >
      <fieldset className="cargo-problem-composer-fields" disabled={isSubmitting}>
        {replyTo ? (
          <div className="cargo-problem-composer-reply">
            <span>{t('message.replyingTo', { author: replyTo.author.name })}</span>
            <IconButton
              icon="x"
              size={24}
              label={t('message.cancelReply')}
              onClick={onCancelReply}
            />
          </div>
        ) : null}
        <Controller
          name="body"
          control={control}
          render={({ field }) => (
            <Textarea
              ref={textareaRef}
              value={field.value ?? ''}
              onChange={field.onChange}
              ariaLabel={t('message.label')}
              rows={3}
              placeholder={t('message.placeholder')}
            />
          )}
        />
        {attachments.items.length > 0 ? (
          <ul className="cargo-problem-composer-attachments">
            {attachments.items.map((item) => {
              const status = t(
                item.status === 'ready'
                  ? 'attachments.statusReady'
                  : item.status === 'error'
                    ? 'attachments.statusError'
                    : 'attachments.statusUploading',
                { name: item.file.name },
              );
              return (
                <li key={item.id} data-status={item.status}>
                  <Icon name={cargoProblemFileIcon(item.file.type)} size={16} />
                  <div className="cargo-problem-attachment-tile-body">
                    <span className="cargo-problem-attachment-name">{item.file.name}</span>
                    <span className="cargo-problem-attachment-meta">
                      <span>{cargoProblemFileType(item.file.type)}</span>
                      <span>{cargoProblemFileSize(item.file.size)}</span>
                      <span role="status" className="cargo-problem-attachment-status">
                        {status}
                      </span>
                    </span>
                    {item.status === 'uploading' ? (
                      // WHY: the bytes ride one `fetch` PUT, which reports no
                      // progress events, so the bar states that the upload is
                      // running instead of inventing a percentage.
                      <progress className="cargo-problem-attachment-progress" aria-label={status} />
                    ) : null}
                  </div>
                  {item.status === 'error' ? (
                    // WHY: the label names the file, so a text button is as wide
                    // as the file name and squeezes the tile to an unreadable
                    // column at 390 px. The icon keeps that name as its
                    // accessible label beside the removal it sits next to.
                    <IconButton
                      icon="rotate-ccw"
                      size={24}
                      label={t('attachments.retry', { name: item.file.name })}
                      onClick={() => attachments.retry(item.id)}
                    />
                  ) : null}
                  <IconButton
                    icon="x"
                    size={24}
                    label={t('attachments.remove', { name: item.file.name })}
                    onClick={() => attachments.remove(item.id)}
                  />
                </li>
              );
            })}
          </ul>
        ) : null}
        {alertText ? (
          <p role="alert" className="cargo-problem-composer-problem">
            {alertText}
          </p>
        ) : null}
        {needsText ? (
          <p className="cargo-problem-composer-hint">{t('message.documentNeedsText')}</p>
        ) : null}
        {!attachments.ready ? (
          <p className="cargo-problem-composer-hint">{t('message.attachmentsPending')}</p>
        ) : null}
        <div>
          {canAttach ? (
            <>
              <input
                ref={inputRef}
                className="cargo-problem-file-input"
                type="file"
                multiple
                tabIndex={-1}
                aria-label={t('attachments.add')}
                onChange={(event) => {
                  const chosen = Array.from(event.target.files ?? []);
                  event.target.value = '';
                  select(chosen);
                }}
              />
              <Button kind="ghost" icon="paperclip" onClick={() => inputRef.current?.click()}>
                {t('attachments.add')}
              </Button>
            </>
          ) : null}
          <Button kind="primary" icon="send-horizontal" disabled={!canSubmit} type="submit">
            {t('message.send')}
          </Button>
        </div>
      </fieldset>
    </form>
  );
}
